import { getProgressRange, getProgressStatus, type ProgressStatus } from '@capsicum/common';
import { type PropsWithChildren } from 'react';
import styled from 'styled-components';

export type CircularChartProps = PropsWithChildren<{
	direction?: 'clockwise' | 'counter-clockwise';
	disabled?: boolean;
	label?: string;
	labelColor?: string;
	progress: number;
	progressCircleColor?: string;
	radius?: number;
}>;

const OFFSET = 6;

/**
 *  This component renders a circular progress chart using svg strokes
 *
 * @param {CircularChartProps} props - The component's props
 * @param {number} [props.progress] - Needed to render the chart color, ranged between 0 and 100
 * @param {number} [props.radius] - Sets the chart size handle by the parent in case it wanted to make it responsive as it is en svg with fixed values
 * @param {string} [props.progressCircleColor] - Sets the color of the progress circle.
 * @param {'clockwise' | 'counter-clockwise'} [props.direction] - Specifies the direction of the circular chart.
 * @param {string} [props.label] - Adds a label to the circular chart.
 * @param {string} [props.labelColor] - Sets the color of the label.
 * @param [props.children] - It has a small center spaced to add show info, date, icon....
 */
export const CircularChart = ({
	children,
	direction = 'counter-clockwise',
	disabled = false,
	label,
	labelColor,
	progress,
	progressCircleColor,
	radius = 24
}: CircularChartProps) => {
	const circumference = radius * 2 * Math.PI;
	const radiusWithOffset = radius + OFFSET;
	const progressRange = getProgressRange(progress);
	const status = getProgressStatus(progressRange);
	const rotationAngle = direction === 'clockwise' ? '90' : '-90';

	const calculatedProgress = ((100 - progressRange) / 100) * circumference;

	return (
		<S.CircularChartContainer>
			<S.ProgressCircleContainer $size={radiusWithOffset * 2} data-testid="circularChart">
				<S.CenteredContent $size={radius * 2 - OFFSET}>{children}</S.CenteredContent>
				<svg height={`${radiusWithOffset * 2}px`} width={`${radiusWithOffset * 2}px`}>
					<S.BackgroundCircle cx={radiusWithOffset} cy={radiusWithOffset} r={radius} />
					<S.Circle
						$circumference={circumference}
						$color={progressCircleColor}
						$disabled={disabled}
						$progress={calculatedProgress}
						$radiusWithOffset={radiusWithOffset}
						$rotationAngle={rotationAngle}
						$status={status}
						cx={radiusWithOffset}
						cy={radiusWithOffset}
						r={radius}
						strokeLinecap="round"
					/>
				</svg>
			</S.ProgressCircleContainer>
			{label && <S.Label $color={labelColor}>{label}</S.Label>}
		</S.CircularChartContainer>
	);
};

namespace S {
	export const CircularChartContainer = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const ProgressCircleContainer = styled.div<{ $size: number }>`
		position: relative;
		text-align: center;
		width: ${({ $size }) => `${$size}px`};
		height: ${({ $size }) => `${$size}px`};
	`;
	export const CenteredContent = styled.div<{ $size: number }>`
		overflow: hidden;
		width: ${({ $size }) => `${$size}px`};
		height: ${({ $size }) => `${$size}px`};
		${({ theme }) => theme.border.radius.circle};
		position: absolute;
		transform: translate(-50%, -50%);
		left: 50%;
		top: 50%;
		display: flex;
		justify-content: center;
		align-items: center;
	`;
	export const Circle = styled.circle<{
		$circumference: number;
		$color?: string;
		$disabled: boolean;
		$progress: number;
		$radiusWithOffset: number;
		$rotationAngle: string;
		$status: ProgressStatus;
	}>`
		fill: none;
		stroke: ${({ $color, $disabled, $status, theme }) =>
			$color || theme.colors.backgrounds.progressStatus[$status][$disabled ? 'subtle' : 'enabled']};
		stroke-width: 4px;
		stroke-dasharray: ${({ $circumference }) => $circumference};
		stroke-dashoffset: ${({ $progress }) => $progress};
		scale: -1 1;
		transform: ${({ $rotationAngle }) => `rotate(${$rotationAngle}deg)`};
		transform-origin: ${({ $radiusWithOffset }) => `${$radiusWithOffset}px ${$radiusWithOffset}px`};
	`;

	export const BackgroundCircle = styled.circle`
		fill: none;
		stroke: ${({ theme }) => theme.colors.backgrounds.status.empty};
		stroke-width: 6px;
	`;

	export const Label = styled.span<{ $color?: string }>`
		${({ theme }) => theme.fonts.headings.title6};
		text-align: center;
		color: ${({ $color, theme }) => ($color ? $color : theme.colors.text.main.enabled)};
	`;
}
